import { useEffect } from 'react';
import { useGetClaims, useDeleteClaim, useGetClaimExpenditure } from '@edison/api-client-react';
import { useQuery } from '@tanstack/react-query';
import { customInstance } from '@edison/api-client-react';
import { AlertCircle, FileText, Plus, Trash2, TriangleAlert, ChevronRight } from 'lucide-react';
import { useLocation } from 'wouter';
import { Button } from '@/components/ui/button.js';
import { Badge } from '@/components/ui/badge.js';
import { Card } from '@/components/ui/card.js';
import { formatFy } from '@/lib/utils.js';
import { useState } from 'react';

function formatAUD(n: number) {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}m`;
  if (n >= 1_000) return `$${Math.round(n / 1_000)}k`;
  return `$${Math.round(n)}`;
}

function useClaimReadiness(claimId: string) {
  return useQuery({
    queryKey: ['claim-readiness', claimId],
    queryFn: () =>
      customInstance<{
        data: {
          checks: Array<{ id: string; state: string }>;
          blockingCount: number;
        };
      }>({ url: `/api/claims/${claimId}/readiness`, method: 'GET' }),
    staleTime: 60_000,
  });
}

interface ClaimItem {
  id: string;
  name: string;
  incomeYear: string;
  status: string;
  createdAt?: string | null;
}

function ClaimCard({
  claim,
  onOpen,
  onDelete,
  deletingId,
  isDeleting,
}: {
  claim: ClaimItem;
  onOpen: () => void;
  onDelete: (e: React.MouseEvent) => void;
  deletingId: string | null;
  isDeleting: boolean;
}) {
  const { data: expenditureData } = useGetClaimExpenditure(claim.id);
  const { data: readinessData } = useClaimReadiness(claim.id);

  const expenditure = (expenditureData?.data as { labour?: { totalRdExpenditure?: number } } | undefined);
  const totalRdExpenditure = expenditure?.labour?.totalRdExpenditure ?? null;

  const checks = readinessData?.data?.checks ?? [];
  const blockingCount = readinessData?.data?.blockingCount ?? 0;
  const satisfiedCount = checks.filter((c) => c.state === 'done').length;
  const totalChecks = checks.length;

  const statusVariant =
    claim.status === 'locked' ? 'positive' :
    claim.status === 'validating' ? 'info' :
    'default';

  const isDelConfirm = deletingId === claim.id;

  return (
    <Card
      variant="interactive"
      onClick={onOpen}
      className="p-4 space-y-3"
    >
      {/* Top row: name + status + delete */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <p className="font-semibold text-sm truncate">{claim.name}</p>
          <Badge variant={statusVariant} className="shrink-0 capitalize">{claim.status}</Badge>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {claim.status === 'draft' && (
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(e); }}
              disabled={isDeleting && isDelConfirm}
              className={`flex items-center justify-center h-6 w-6 rounded border transition-colors ${
                isDelConfirm
                  ? 'border-destructive bg-destructive text-destructive-foreground'
                  : 'border-border text-muted-foreground hover:border-destructive hover:text-destructive'
              }`}
              aria-label={isDelConfirm ? 'Click again to confirm delete' : 'Delete draft claim'}
              title={isDelConfirm ? 'Click again to confirm' : 'Delete draft claim'}
            >
              <Trash2 className="h-3 w-3" aria-hidden="true" />
            </button>
          )}
          <ChevronRight className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        </div>
      </div>

      {/* Income year */}
      <p className="text-xs text-muted-foreground -mt-1">{formatFy(claim.incomeYear)}</p>

      {/* Expenditure + offset row */}
      <div className="flex items-end gap-6 pt-1 border-t border-border/40">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-0.5">Expenditure</p>
          <p className="text-sm font-semibold font-mono tabular-nums">
            {totalRdExpenditure != null ? formatAUD(totalRdExpenditure) : '—'}
          </p>
        </div>
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-0.5">Est. offset (43.5%)</p>
          <p className="text-sm font-semibold font-mono tabular-nums text-emerald-600 dark:text-emerald-400">
            {totalRdExpenditure != null ? formatAUD(totalRdExpenditure * 0.435) : '—'}
          </p>
        </div>
        <div className="ml-auto">
          {totalChecks > 0 ? (
            <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full ${
              blockingCount > 0
                ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
            }`}>
              {blockingCount > 0 && <TriangleAlert className="h-3 w-3" aria-hidden="true" />}
              {satisfiedCount} of {totalChecks} checks
            </span>
          ) : null}
        </div>
      </div>
    </Card>
  );
}

export function ClaimListPage() {
  const [, navigate] = useLocation();
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const { data, isLoading, error, refetch } = useGetClaims();
  const { mutate: deleteClaim, isPending: isDeleting } = useDeleteClaim();

  const claims = data?.data?.items ?? [];

  // Auto-redirect: if there's exactly one active (non-locked) claim, go to it directly
  useEffect(() => {
    if (isLoading || claims.length === 0) return;
    const active = claims.filter((c) => c.status !== 'locked');
    if (active.length === 1 && claims.length === 1) {
      navigate(`/claims/${active[0]!.id}`, { replace: true });
    }
  }, [isLoading, claims, navigate]);

  const handleDelete = (e: React.MouseEvent, claimId: string) => {
    e.stopPropagation();
    if (deletingId === claimId) {
      deleteClaim({ id: claimId }, { onSuccess: () => { setDeletingId(null); void refetch(); } });
    } else {
      setDeletingId(claimId);
      setTimeout(() => setDeletingId((prev) => prev === claimId ? null : prev), 3000);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-48 rounded-lg skeleton" />
        {[...Array(2)].map((_, i) => (
          <div key={i} className="h-28 w-full rounded-lg skeleton" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 flex items-center gap-2 text-destructive text-sm">
        <AlertCircle className="h-4 w-4 shrink-0" />
        <span>Failed to load claims.</span>
        <button onClick={() => refetch()} className="ml-auto underline text-xs">Retry</button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between" data-tour="claims-list-header">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">R&D Claims</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage R&DTI claims for each income year.
          </p>
        </div>
        <Button variant="primary" onClick={() => navigate('/claims/new')}>
          <Plus className="h-4 w-4" aria-hidden="true" />
          New claim
        </Button>
      </div>

      {claims.length === 0 ? (
        <div className="text-center py-12 rounded-lg border border-dashed">
          <FileText className="h-10 w-10 text-muted-foreground mx-auto mb-3" aria-hidden="true" />
          <p className="font-medium">No claims yet</p>
          <p className="text-sm text-muted-foreground mt-1">
            Create a claim to start building your R&DTI evidence package.
          </p>
          <Button variant="primary" className="mt-4" onClick={() => navigate('/claims/new')}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            New claim
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {claims.map((claim) => (
            <ClaimCard
              key={claim.id}
              claim={claim}
              onOpen={() => navigate(`/claims/${claim.id}`)}
              onDelete={(e) => handleDelete(e, claim.id)}
              deletingId={deletingId}
              isDeleting={isDeleting}
            />
          ))}
        </div>
      )}
    </div>
  );
}
